import { useState, useEffect, useRef } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { setLanguage, getLanguage } from "@/lib/translator";

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "tr", label: "Türkçe" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ไทย" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "pl", label: "Polski" },
  { code: "uk", label: "Українська" },
  { code: "el", label: "Ελληνικά" },
  { code: "he", label: "עברית" },
  { code: "sv", label: "Svenska" },
  { code: "fil", label: "Filipino" },
  { code: "fa", label: "فارسی" },
  { code: "ro", label: "Română" },
];

export default function LanguageSwitcher({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(getLanguage());
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const choose = async (code) => {
    setOpen(false);
    if (code === lang) return;
    setLang(code);
    setBusy(true);
    await setLanguage(code); // 'en' reloads; others translate live
    setBusy(false);
  };

  return (
    <div className="relative" ref={ref} data-no-translate data-testid={`language-switcher-${variant}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-btn"
        className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-brand transition-colors rounded-lg px-2.5 py-1.5 border border-slate-200 hover:border-brand"
        aria-label="Change language"
      >
        <Globe size={15} className={busy ? "animate-spin" : ""} />
        <span className="hidden sm:inline">{current.label}</span>
        <span className="sm:hidden uppercase">{current.code}</span>
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div className={`absolute ${variant === "desktop" ? "right-0" : "left-0"} mt-2 w-52 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-xl soft-shadow p-1.5 z-[60]`} data-testid="language-menu">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full text-left px-3 py-2 text-sm rounded-lg flex items-center justify-between transition-colors ${l.code === lang ? "text-brand bg-blue-50 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}
            >
              {l.label}
              {l.code === lang && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
